// Date helpers, Danish holidays and birthdays for the calendar views (Monday-first weeks, 24h clock)

export const DOW    = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
export const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
export const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export const COLORS = ['#0e639c', '#2e9d5b', '#c0392b', '#d68910', '#8e44ad', '#16a085', '#d35400', '#c2185b', '#607d8b'];

export const pad = n => String(n).padStart(2, '0');

export function startOfWeek(date) {
  const d = new Date(date);
  const day = d.getDay();
  d.setDate(d.getDate() + (day === 0 ? -6 : 1 - day));
  d.setHours(0, 0, 0, 0);
  return d;
}
export function startOfDay(date) { const d = new Date(date); d.setHours(0, 0, 0, 0); return d; }
export function endOfDay(date)   { const d = new Date(date); d.setHours(23, 59, 59, 999); return d; }
export function addDays(date, n) { const d = new Date(date); d.setDate(d.getDate() + n); return d; }
export function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
export function isoWeek(date) {
  const d = startOfDay(date);
  d.setDate(d.getDate() + 4 - (d.getDay() || 7));
  return Math.ceil(((d - new Date(d.getFullYear(), 0, 1)) / 86400000 + 1) / 7);
}

export const fmtTime = d => `${pad(d.getHours())}:${pad(d.getMinutes())}`;
export const fmtDate = d => `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;

// Values for <input type="date"> / <input type="time">
export const toDateInput = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export const toTimeInput = d => `${pad(d.getHours())}:${pad(d.getMinutes())}`;

// Does the event overlap the given day?
export function onDay(ev, day) {
  return new Date(ev.start) <= endOfDay(day) && new Date(ev.end) >= startOfDay(day);
}

// ── Danish holidays ──────────────────────────────────────────────────────────
function easterSunday(year) {
  const a = year % 19, b = Math.floor(year / 100), c = year % 100;
  const d = Math.floor(b / 4), e = b % 4, f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4), k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31) - 1;
  const day   = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month, day);
}

const allDayEvent = (id, title, date, calendar, color) => ({
  _id: id, title, calendar, color, allDay: true, readOnly: true,
  start: startOfDay(date), end: endOfDay(date),
});

export function danishHolidays(year, color) {
  const e = easterSunday(year);
  return [
    ['Nytårsdag',             new Date(year, 0, 1)],
    ['Skærtorsdag',           addDays(e, -3)],
    ['Langfredag',            addDays(e, -2)],
    ['Påskedag',              e],
    ['2. Påskedag',           addDays(e, 1)],
    ['Kristi Himmelfartsdag', addDays(e, 39)],
    ['Pinsedag',              addDays(e, 49)],
    ['2. Pinsedag',           addDays(e, 50)],
    ['Grundlovsdag',          new Date(year, 5, 5)],
    ['Juledag',               new Date(year, 11, 25)],
    ['2. Juledag',            new Date(year, 11, 26)],
  ].map(([name, date]) => allDayEvent(`dh-${year}-${name}`, name, date, 'holidays', color));
}

// people: [{ _id, name, dateOfBirth }] → one all-day event per person in `year`, with their age
export function birthdays(people, year, color) {
  return people.map(p => {
    // Dates of birth are stored as UTC midnight (from <input type="date">)
    const dob = new Date(p.dateOfBirth);
    const daysInMonth = new Date(year, dob.getUTCMonth() + 1, 0).getDate();
    const date = new Date(year, dob.getUTCMonth(), Math.min(dob.getUTCDate(), daysInMonth)); // 29 Feb → 28 Feb
    const age = year - dob.getUTCFullYear();
    return allDayEvent(`bd-${p._id}-${year}`, `🎂 ${p.name}${age > 0 ? ` (${age})` : ''}`, date, 'birthdays', color);
  });
}

// Generated events for every year the range touches, clipped to the range
export function generatedInRange(start, end, make) {
  const out = [];
  for (let y = start.getFullYear(); y <= end.getFullYear(); y++) {
    for (const ev of make(y)) if (ev.start <= end && ev.end >= start) out.push(ev);
  }
  return out;
}

// Side-by-side columns for overlapping timed events in one day column
export function layoutColumns(events) {
  const sorted = [...events].sort((a, b) => new Date(a.start) - new Date(b.start) || new Date(b.end) - new Date(a.end));
  const result = new Map();
  let cluster = [], colsEnd = [], clusterEnd = 0;
  const flush = () => { cluster.forEach(ev => result.get(ev).cols = colsEnd.length); cluster = []; colsEnd = []; };
  for (const ev of sorted) {
    const s = +new Date(ev.start), e = Math.max(+new Date(ev.end), s + 20 * 60000);
    if (cluster.length && s >= clusterEnd) flush();
    let col = colsEnd.findIndex(end => end <= s);
    if (col === -1) { col = colsEnd.length; colsEnd.push(e); } else colsEnd[col] = e;
    result.set(ev, { col, cols: 1 });
    cluster.push(ev);
    clusterEnd = Math.max(clusterEnd, e);
  }
  flush();
  return result;
}
